import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { CountryModel } from 'src/app/models/country.model';
import { Icons } from '../../../models/icons.model';
import { SiteModel } from '../../../models/site.model';
import { UserSearchRequest, UserSearchResult } from '../../../models/user-search-request.model';
import { UserModel } from '../../../models/user.model';
import { AdminService } from '../../../services/admin.service';
import { UserService } from '../../../services/user.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { AdSearchComponent } from '../../components/ad-search/ad-search.component';
import parsePhoneNumber, { CountryCode } from 'libphonenumber-js'

@Component({
  selector: 'app-admin-user-detail',
  standalone: false,
  templateUrl: './admin-user-detail.component.html',
  styleUrls: ['./admin-user-detail.component.scss'],
})
export class AdminUserDetailComponent implements OnInit, AfterViewInit {
  /**
   * Gets if an site is selected
   * @returns boolean
   */
  get siteSelected(): boolean {
    return !this.isReady || this.sites?.filter((s) => s.IsSelected).length > 0 || this.user.IsAdmin;
  }

  // Variables
  adminServiceLoadEvent: Subscription;
  alertMsg: string;
  countries: CountryModel[] = [];
  icons = new Icons();
  isNew: boolean;
  isReady: boolean;
  // selectedCountry: CountryModel;
  sites: SiteModel[] = [];
  user: UserModel = new UserModel();
  selectedCountryId: number | null | undefined = null;

  constructor(
    private userService: UserService,
    private adminService: AdminService,
    private route: ActivatedRoute,
    private router: Router,
    private modal: NgbModal
  ) { }

  ngOnInit(): void {
    this.isReady = false;
    this.alertMsg = 'Loading user...';

    this.route.paramMap.subscribe((params) => {
      if (params.has('id')) {
        if (params.get('id') === 'add') {
          // adding a new user
          if (this.adminService.isReady) this.initNew();
          else {
            this.adminServiceLoadEvent = this.adminService.adminServiceLoadedEvent.subscribe(
              () => {
                this.adminServiceLoadEvent.unsubscribe();
                this.initNew();
              }
            );
          }
        } else {
          // editing a existing user
          const userId: number = +params.get('id');

          if (this.adminService.isReady) this.initEdit(userId);
          else {
            this.adminServiceLoadEvent = this.adminService.adminServiceLoadedEvent.subscribe(
              () => {
                this.adminServiceLoadEvent.unsubscribe();
                this.initEdit(userId);
              }
            );
          }
        }
      } else {
        // redirect back to user list
        this.router.navigate(['/admin', 'users']);
      }
    });
  }

  /**
   * Initialize a new user
   * @returns void
   */
  initNew(): void {
    // Gets the all sites
    this.sites = JSON.parse(JSON.stringify(this.adminService.sites));

    // Gets the all countries
    this.countries = JSON.parse(JSON.stringify(this.adminService.countries));
    this.isNew = true;
    this.sites.forEach((s) => (s.IsSelected = false));
    this.isReady = true;

    // Open Add User Modal Search
    this.openAdUserSearch();
  }

  /**
   * Initialize an edit user
   * @param  {number} userId
   * @returns void
   */
  initEdit(userId: number): void {
    // Gets the all sites
    this.sites = JSON.parse(JSON.stringify(this.adminService.sites));

    // Gets the all countries
    this.countries = JSON.parse(JSON.stringify(this.adminService.countries));

    // Get the user information
    this.getUserById(userId);

    this.isReady = true;
  }

  /**
   * Handle the change country
   * @param  {any} country
   */
  changeCountry(country: any): void {

    try {
      // Clear the mobile phone field
      this.clearPhoneMask();

      //Gets the selected country
      let selectedCountry = this.countries.find(c => c.Id == country)

      // Update the mobile user
      this.user.Country = selectedCountry;
      this.user.CountryId = selectedCountry ? selectedCountry.Id : 0;
    }
    catch (error) {
      console.error("Error on change country: ", error);
    }
  }

  /**
   * @returns void
   */
  clearPhoneMask(): void {
    // Remove the phone code from mobile phone
    this.user.Mobile = this.user.Mobile ? this.user.Mobile.substring(this.user.Mobile.indexOf(' ') + 1) : "";

    // Remove from mobile phone the () and space in blank
    this.user.Mobile = this.user.Mobile ? this.user.Mobile.replace(/[()\ \s-]+/g, '') : "";
  }

  /**
   * Gets the specific user information
   * @param  {number} userId - The user id
   * @returns void
   */
  getUserById(userId: number): void {
    Promise.all([
      this.userService.getUser(userId),
    ])
      .then((results) => {
        // Saving the result to the user var
        this.user = results[0];

        // Populate the user's sites
        this.sites.forEach((site) => {
          const us = this.user.Sites.find((s) => s.Id == site.Id);
          site.IsSelected = us ? true : false;
        });

        // Sets the country id
        this.selectedCountryId = this.user.Country.Id;

        // Set the phone mask
        this.setUserMobilePhone(this.user.Mobile || '');
        // this.setPhoneMask(results[0].Country);
      })
      .catch((err) => {
        console.error('Error getting user info: ', err);
      })
  }

  /**
   * Open the user search modal
   * @returns void
   */
  openAdUserSearch(): void {
    const searchModalRef = this.modal.open(AdSearchComponent, {
      size: 'lg',
      keyboard: true,
      scrollable: true,
    });

    searchModalRef.result
      .then((selectedUser: UserSearchResult) => {
        this.user.Name = `${selectedUser.GivenName} ${selectedUser.Surname}`;
        this.user.EmailAddress = selectedUser.Mail;
        this.user.Upn = selectedUser.UserPrincipalName;

        // Parse and format phone number
        this.setUserMobilePhone(selectedUser.MobilePhone || '')

      })
      .catch((result) => {
        // search canceled
      });
  }

  /**
   * Parses and sets the user's mobile phone number
   * @param {string} phoneNumber - The phone number to set
   * @returns void
   */
  setUserMobilePhone(phoneNumber: string): void {

    if (!phoneNumber || phoneNumber.trim() === '') {
      this.user.Mobile = '';
      return;
    }

    const countryCode = this.user.Country.Code as CountryCode || 'US';
    const p = parsePhoneNumber(phoneNumber, countryCode); // Default to US if country not known
    if (p && p.isValid()) {
      this.user.Mobile = p.formatInternational();
      const country = this.countries.find(c => c.Code === p.country);
      if (country) {
        this.user.Country = country;
        this.selectedCountryId = country.Id;
      }
    }
    else {
      console.warn('Invalid phone number: ', phoneNumber);
    }
  };

  findUserMobilePhone(): void {
    const searchRequest = new UserSearchRequest();
    searchRequest.UserPrincipalName = this.user.Upn;

    const result = this.adminService.searchForUser(searchRequest);

    result.then(users => {
      if (users.length > 0) {
        const user = users[0];
        if (!user.MobilePhone) {
          alert('The selected user does not have a mobile phone number in Active Directory.');
          return;
        }

        this.setUserMobilePhone(user.MobilePhone || '');
      }
      else {
        console.warn('No user found for UPN: ', this.user.Upn);
        alert(`No user found with the specified UPN: [${this.user.Upn}].`);
      }
    }).catch(err => {
      console.error('Error finding user mobile phone: ', err);
    });


  }

  ngAfterViewInit(): void {
    const element = document.querySelector('#txtName');
  }

  /**
   * Save a user
   * @returns void
   */
  saveUser(): void {
    let promise: Promise<UserModel>;
    this.isReady = false;
    this.alertMsg = 'Saving user...';

    // Map the selected sites to the user
    if (this.user.IsAdmin) {
      this.user.Sites = [];
    } else {
      this.user.Sites = this.sites.filter((s) => s.IsSelected);
    }

    if (this.isNew) {
      promise = this.userService.addUser(this.user);
    } else {
      promise = this.userService.updateUser(this.user);
    }
    promise.then((user) => {
      this.adminService.reloadService();
      this.router.navigate(['/admin', 'users']);
    }).catch((err) => {
      console.error('Error saving user: ', err);
      alert(`Error saving user.\r\n${err.error?.detail || err.message}`);
    }).finally(() => {
      this.isReady = true;
    });
  }

  /**
   * Delete a user
   * @returns void
   */
  deleteUser(): void {
    const confirm = this.modal.open(ConfirmDialogComponent, {
      keyboard: true,
      centered: true,
    });

    confirm.componentInstance.dialogTitle = 'Delete User';
    confirm.componentInstance.dialogMessage =
      'Are you sure you want to delete this user?';

    confirm.result
      .then(() => {
        this.alertMsg = 'Deleting user...';
        this.isReady = false;

        this.userService.removeUser(this.user.Id).then(() => {
          this.adminService.reloadService();
          this.router.navigate(['/admin', 'users']);
        });
      })
      .catch(() => {
        console.log('canceled');
      });
  }

  /**
   * Select all sites
   * @returns void
   */
  selectAll(): void {
    this.sites.forEach((s) => (s.IsSelected = true));
  }

  /**
   * Deselect all sites
   * @returns void
   */
  selectNone(): void {
    this.sites.forEach((s) => (s.IsSelected = false));
  }
}
